import { useI18n } from '@ecom/i18n';
import { Button, Field, Input } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { useLoginMutation } from '../api';
import { AuthLayout } from '../components/AuthLayout';
import { translateFieldError } from '../field-error';
import { loginFormSchema, type LoginFormValues } from '../schemas';

import { ApiError } from '@/lib/api-client';
import { whenDefined } from '@/lib/optional-props';
import { useSession } from '@/lib/session';

export function LoginPage(): ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setSession } = useSession();
  const loginMutation = useLoginMutation();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({ resolver: zodResolver(loginFormSchema) });

  async function onSubmit(values: LoginFormValues): Promise<void> {
    setFormError(null);
    try {
      const result = await loginMutation.mutateAsync(values);
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        mustChangePassword: result.mustChangePassword,
      });
      // A provisional password may only be used to choose a real one: straight to that screen.
      void navigate(result.mustChangePassword ? '/change-password' : '/dashboard', {
        replace: true,
      });
    } catch (error) {
      // The API deliberately gives ONE answer for every rejection cause (unknown address, wrong password, locked account,
      // rate limited — auth.controller.ts). This page does the same: one sentence, in the person's language, that names
      // none of the causes — which would reopen exactly the enumeration channel the API closes. Only « no answer at all »
      // is told apart, because that says nothing about any account.
      setFormError(error instanceof ApiError ? t('auth.login.invalid') : t('errors.network'));
    }
  }

  return (
    <AuthLayout
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
      // Nobody registers any more (lot P4): say where an account comes from instead of pointing at a door that is gone.
      footer={<>{t('auth.login.noAccountFooter')}</>}
    >
      <p role="note" className="mb-4 rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
        {t('auth.login.platformAccountNotice')}
      </p>
      <form
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <Field
          label={t('auth.email.label')}
          {...whenDefined('error', translateFieldError(t, errors.email?.message))}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('email')}
              type="email"
              dir="ltr"
              autoComplete="email"
              autoFocus
            />
          )}
        </Field>

        <Field
          label={t('auth.password.label')}
          {...whenDefined('error', translateFieldError(t, errors.password?.message))}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('password')}
              type="password"
              autoComplete="current-password"
            />
          )}
        </Field>

        <Link to="/forgot-password" className="-mt-2 self-end text-xs text-accent hover:underline">
          {t('auth.login.forgot')}
        </Link>

        {formError !== null ? (
          <p role="alert" className="rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
            {formError}
          </p>
        ) : null}

        <Button type="submit" loading={loginMutation.isPending} className="mt-1 w-full">
          {t('auth.login.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
